import './FinanciarWizard.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BackButton, ContinueButton } from '../../components/buttons/Buttons';
import EntradaMoneda from '../../components/currencyInput/EntradaMoneda';
import { postClientData } from '../../services/apiService';
import { saveToSessionStorage } from '../../services/storageService';
import { formatPhone, formatRUT, getYesterdayDate, isValidRUT } from '../../utils/formUtils';
import texts from '../../utils/texts.json';
import useTrackEvent from '../../utils/trackEvent';
import values from '../../utils/values.json';

const MINIMUM_AMOUNT = 600000;

const ContactForm = ({ onNextStep, data, isMeliTenant }) => {
  const { contactForm } = texts;
  const hasPushedPageView = useRef(false);
  const { pushPageView, pushCustomEvent } = useTrackEvent();
  const formRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    rut: data?.rut || '',
    name: data?.name || '',
    surnames: data?.surnames || '',
    phone: data?.phone || '+56 9',
    email: data?.email || '',
    workerType: data?.workerType || '',
    salary: data?.salary || '',
    startWorkingDate: data?.startWorkingDate || '',
    model: 'CLA 250',
    brand: 'Mercedes Benz',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputErrors, setInputErrors] = useState({});

  useEffect(() => {
    if (!hasPushedPageView.current) {
      pushPageView(
        'vpv',
        'Crédito Automotríz',
        '1',
        '/atencion-normal/info-personal',
        'Información personal - Atención Normal'
      );

      hasPushedPageView.current = true;
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'rut') formattedValue = formatRUT(value);
    if (name === 'phone') formattedValue = formatPhone(value);
    if (name === 'salary') {
      formattedValue = value.replace(/\D/g, '');
      formattedValue = formattedValue.slice(0, 8);
    }

    if (name === 'startWorkingDate') {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        setInputErrors((prev) => ({
          ...prev,
          startWorkingDate: 'La fecha no puede ser futura.',
        }));
      } else {
        setInputErrors((prev) => ({
          ...prev,
          startWorkingDate: '',
        }));
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: formattedValue,
    }));

    handleBlur(e);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);

    setInputErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const validateField = (name, value) => {
    if (!value) return 'Campo requerido';

    if (name === 'phone') {
      const phone = value.replace('+56', '').replace(/\s+/g, '');
      if (value === '+56 9' || phone.length < 9) {
        return 'Ingrese un número de teléfono válido';
      }
    }

    if ((name === 'name' || name === 'surnames') && !/^[a-zA-ZÁÉÍÓÚÑáéíóúñ\s]+$/.test(value)) {
      return 'Solo se permiten letras y espacios';
    }

    if (name === 'rut' && !isValidRUT(value)) {
      return 'Por favor, ingresa un RUT válido';
    }

    if (name === 'salary') {
      const salaryValue = parseInt(String(value).replace(/\D/g, ''));

      if (salaryValue < MINIMUM_AMOUNT) {
        return `La renta mínima debe ser al menos ${MINIMUM_AMOUNT}`;
      }
    }

    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Por favor, ingresa un correo electrónico válido.';
      }
    }

    if (name === 'startWorkingDate') {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Asegura comparación solo por fecha, sin hora

      if (selectedDate > today) {
        setInputErrors((prev) => ({
          ...prev,
          startWorkingDate: 'La fecha no puede ser futura.',
        }));
      } else {
        setInputErrors((prev) => ({
          ...prev,
          startWorkingDate: '',
        }));
      }
    }

    return '';
  };

  const validateForm = () => {
    const errors = {};

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) errors[key] = error;
    });

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    pushCustomEvent('event_buttons', {
      flujo: 'Crédito Automotríz',
      button_step_value: '1',
      step: '1',
      page: '/atencion-normal/info-personal',
      PageName: 'Información personal - Atención Normal',
      button_step_name: 'continuar',
      version: 'v2',
    });

    setIsLoading(true);
    setError(null);

    const validationError = validateForm();
    if (!validationError) {
      setIsLoading(false);
      return;
    }

    const salaryAsString =
      typeof formData.salary === 'string' ? formData.salary.replace(/\D/g, '') : formData.salary;

    const submittedData = {
      ...formData,
      typeFinancing: 'financiamiento',
      salary: parseInt(salaryAsString),
    };

    saveToSessionStorage('formData', formData);

    try {
      const response = await postClientData(submittedData);
      sessionStorage.setItem('formData', JSON.stringify(response));
      onNextStep(response);
    } catch (error) {
      setError('Error al enviar los datos, por favor inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="financiar-wizard-contact-form">
      <h1>{contactForm.title}</h1>
      <p className="margin-bottom-15">{contactForm.subTitle}</p>
      <form ref={formRef} onSubmit={handleSubmit} className="mui-form">
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.rut ? '-input-error' : ''
            }`}
          >
            {contactForm.rut.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              name="rut"
              placeholder={contactForm.rut.placeholder}
              type="text"
              maxLength={12}
              className={`form-input${inputErrors.rut ? '-input-error' : ''}`}
              value={formData.rut}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.rut && <span className="error-required-input-field">{inputErrors.rut}</span>}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.rut ? '-input-error' : ''
            }`}
          >
            {contactForm.name.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              name="name"
              placeholder={contactForm.name.placeholder}
              type="text"
              maxLength={30}
              className={`form-input${inputErrors.name ? '-input-error' : ''}`}
              value={formData.name}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.name && (
            <span className="error-required-input-field">{inputErrors.name}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.surnames ? '-input-error' : ''
            }`}
          >
            {contactForm.lastName.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              name="surnames"
              placeholder={contactForm.lastName.placeholder}
              type="text"
              maxLength={60}
              className={`form-input${inputErrors.surnames ? '-input-error' : ''}`}
              value={formData.surnames}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.surnames && (
            <span className="error-required-input-field">{inputErrors.surnames}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.phone ? '-input-error' : ''
            }`}
          >
            {contactForm.celPhone.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              type="tel"
              name="phone"
              placeholder={contactForm.celPhone.placeholder}
              className={`form-input${inputErrors.phone ? '-input-error' : ''}`}
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={handleBlur}
              maxLength="17"
              required
            />
          </div>
          {inputErrors.phone && (
            <span className="error-required-input-field">{inputErrors.phone}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.email ? '-input-error' : ''
            }`}
          >
            {contactForm.email.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              type="email"
              name="email"
              placeholder={contactForm.email.placeholder}
              className={`form-input${inputErrors.email ? '-input-error' : ''}`}
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.email && (
            <span className="error-required-input-field">{inputErrors.email}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.workerType ? '-input-error' : ''
            }`}
          >
            {contactForm.employmentRelationship.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="workerType"
              className={`worker-type${inputErrors.workerType ? '-input-error' : ''}`}
              value={formData.workerType}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            >
              <option value="" disabled hidden>
                Seleccionar
              </option>
              {values.ocupacion.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {inputErrors.workerType && (
            <span className="error-required-input-field">{inputErrors.workerType}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.salary ? '-input-error' : ''
            }`}
          >
            {contactForm.netIncome.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              type="text"
              name="salary"
              placeholder={contactForm.netIncome.placeholder}
              min={600000}
              className={`form-input${inputErrors.salary ? '-input-error' : ''}`}
              value={formData.salary}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.salary && (
            <span className="error-required-input-field">{inputErrors.salary}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.startWorkingDate ? '-input-error' : ''
            }`}
          >
            {contactForm.employmentStartDate.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              type="date"
              name="startWorkingDate"
              className={`form-input${inputErrors.startWorkingDate ? '-input-error' : ''}`}
              value={formData.startWorkingDate}
              onChange={handleInputChange}
              onBlur={handleBlur}
              max={getYesterdayDate()}
              required
            />
          </div>
          {inputErrors.startWorkingDate && (
            <span className="error-required-input-field">{inputErrors.startWorkingDate}</span>
          )}
        </div>

        {error && <div className="error-msg">{error}</div>}

        <div className="nav-button-container">
          <BackButton onClick={() => navigate(-1)} />
          <ContinueButton type="submit" isLoading={isLoading} />
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
